import { Feature } from '@/pinia/feature'

const energyConsumptionColorScale = {
  type: 'INTERPOLATE',
  cutoffs: [
    { value: 0, color: '#40903a' },
    { value: 20, color: '#6da930' },
    { value: 40, color: '#cad228' },
    { value: 60, color: '#fbee30' },
    { value: 80, color: '#f1be26' },
    { value: 100, color: '#da711c' },
    { value: 120, color: '#d02920' }
  ]
}

const energyProductionColorScale = {
  type: 'INTERPOLATE',
  cutoffs: [
    { value: 0, color: '#d02920' },
    { value: 10, color: '#da711c' },
    { value: 20, color: '#f1be26' },
    { value: 30, color: '#fbee30' },
    { value: 40, color: '#cad228' },
    { value: 50, color: '#6da930' },
    { value: 60, color: '#40903a' }
  ]
}

const createDataSource = (name: string, index: number) => ({
  id: `energy.measurement.${name}.value`,
  type: 'fetch',
  fetch: 'fetch.energy.actual.measurements',
  path: `kpis[${index}].values[0].value`
})

const createDataSourceColoredEntities = (name: string, index: number) => ({
  id: `energy.measurement.${name}.colored-entities`,
  type: 'fetch',
  fetch: 'fetch.energy.actual.measurements',
  transform: {
    id: 'energy.actual.colored-entities',
    index
  }
})

const createMeasurementDetails = (name: string) => {
  return {
    id: `energy.measurement.${name}.detail`,
    type: 'fetch',
    fetch: `fetch.energy.actual.${name}.detail`,
    transform: {
      id: 'energy.actual.detail',
      format: format,
      detailRowNames: {
        ELECTRICITY: {
          sv: 'El',
          en: 'Electricity'
        },
        GAS: {
          sv: 'Gas',
          en: 'Gas'
        },
        STEAM: {
          sv: 'Ånga',
          en: 'Steam'
        },
        DISTRICT_HEATING: {
          sv: 'Fjärrvärme',
          en: 'District heating'
        },
        COOLING: {
          sv: 'Kyla',
          en: 'Cooling'
        },
        SOLAR_ELECTRICITY: {
          sv: 'Solenergi',
          en: 'Solar energy'
        }
      }
    }
  }
}

const titles = {
  total: {
    sv: 'Använd energi',
    en: 'Used energy'
  },
  totalOverArea: {
    sv: 'Använd energi / m²',
    en: 'Used energy / m²'
  },
  bought: {
    en: 'Bought energy',
    sv: 'Inköpt energi'
  },
  boughtOverArea: {
    en: 'Bought energy / m²',
    sv: 'Inköpt energi / m²'
  },
  production: {
    sv: 'Producerad energi',
    en: 'Produced energy'
  },
  productionOverArea: {
    sv: 'Producerad energi / m²',
    en: 'Produced energy / m²'
  },
  construction: {
    en: 'Construction energy',
    sv: 'Fastighetsenergi'
  },
  tenant: {
    en: 'Tenant energy',
    sv: 'Hyresgästsenergi'
  }
}

const infos = {
  total: {
    sv: 'Avser den energi som används inom byggnadens klimatskal. Till skillnad från den inköpta energin är det värmen från en värmepump som ingår och inte elen om driver värmepumpen.',
    en: 'Refers to the energy used within the building envelope. Unlike the purchased energy, it is the heat from a heat pump that is included and not the electricity that drives the heat pump.'
  },
  totalOverArea: {
    sv: 'Avser den energi som används inom byggnadens klimatskal per ytenhet. Till skillnad från den inköpta energin är det värmen från en värmepump som ingår och inte elen om driver värmepumpen.',
    en: 'Refers to the energy used within the building envelope per area unit. Unlike the purchased energy, it is the heat from a heat pump that is included and not the electricity that drives the heat pump.'
  },
  bought: {
    sv: 'Avser den energi som är inköpt från extern leverantör och som används inom Akademiska Hus byggnader. Typen används för områdestyperna Byggnad och Campus (Leveransområde). För Campus avses den energi som köpts inklusive transformerings-och distributionsförluster. För byggnad avses den använda energi som uppmätts vid den specifika byggnaden',
    en: 'Refers to the energy that is purchased from an external supplier and used within Akademiska Hus buildings. The type is used for the area types Building and Campus (Delivery area). For Campus, the energy purchased including transformation and distribution losses is meant. For building, the energy used is measured at the specific building.'
  },
  boughtOverArea: {
    sv: 'Avser den energi som är inköpt från extern leverantör och som används inom Akademiska Hus byggnader per ytenhet. Typen används för områdestyperna Byggnad och Campus (Leveransområde). För Campus avses den energi som köpts inklusive transformerings-och distributionsförluster. För byggnad avses den använda energi som uppmätts vid den specifika byggnaden',
    en: 'Refers to the energy that is purchased from an external supplier and used within Akademiska Hus buildings per area unit. The type is used for the area types Building and Campus (Delivery area). For Campus, the energy purchased including transformation and distribution losses is meant. For building, the energy used is measured at the specific building.'
  },
  production: {
    sv: 'Avser den energi som produceras och inte i första hand är en transformering. Exempelvis solenergi, marklager samt värme eller kyla från en värmepump.',
    en: 'Refers to the energy that is produced and is not primarily a transformation. For example, solar energy, ground layers, and heat or cold from a heat pump.'
  },
  productinOverArea: {
    sv: 'Avser den energi som produceras och inte i första hand är en transformering per ytenhet. Exempelvis solenergi, marklager samt värme eller kyla från en värmepump.',
    en: 'Refers to the energy that is produced and is not primarily a transformation per area unit. For example, solar energy, ground layers, and heat or cold from a heat pump.'
  },
  construction: {
    sv: 'Avser all inköpt energi exklusive hyresgästenergi. Fastighetsenergi är användningen vid varje given tidpunkt och utgår inte enbart från då byggnad är tom eller fullt uthyrd.',
    en: 'Refers to all purchased energy excluding tenant energy. Property energy is the use at any given time and is not based solely on when the building is empty or fully rented.'
  },
  tenant: {
    sv: 'Avser den energianvändning som ej är fastighetsenergi. Dvs den energi som ej används i fasta installationer för ventilation och uppvärmning av byggnaden samt drift av servicefunktioner, t.ex. hissar, tvättstugor och allmän belysning.',
    en: 'Refers to the energy use that is not property energy. That is, the energy that is not used in fixed installations for ventilation and heating of the building and operation of service functions, e.g. elevators, laundry rooms, and general lighting.'
  }
}

const format = {
  format: {
    type: 'intl-number',
    maximumFractionDigits: 0
  },
  steps: [{ format: { type: 'format-string', format: '%s kWh' } }]
}

const createMeasurement = (
  name: string,
  first: boolean = false,
  color: string = '#6a6a6a',
  enabled = true
) => ({
  name,
  title: titles[name],
  info: infos[name],
  enabled,
  component: 'MainMeasurementCard',
  props: {
    initialShowOnMap: first,
    value: {
      dataSource: `energy.measurement.${name}.value`,
      format,
      isLoading: false,
      textType: 'h4'
    },
    coloredEntities: {
      dataSource: `energy.measurement.${name}.colored-entities`
    },
    colorScale: name.startsWith('production')
      ? energyProductionColorScale
      : energyConsumptionColorScale,
    colorLegend: {
      hideMean: false
    },
    table: {
      dataSource: `energy.measurement.${name}.detail`,
      headers: [
        {
          sv: 'Energislag',
          en: 'Energy type'
        },
        {
          sv: 'kWh',
          en: 'kWh'
        }
      ]
    },
    color
  },
  children: [
    {
      name: 'legend',
      enabled: true,
      component: 'InteractiveColorLegend',
      title: titles[name],
      props: name.startsWith('production')
        ? {
            fromColor: '#FF4D4F',
            toColor: '#73D13D',
            middleColor: '#40A9FF',
            format
          }
        : {
            fromColor: '#73D13D',
            toColor: '#FF4D4F',
            middleColor: '#40A9FF',
            format
          }
    }
  ]
})

export const actualEnergy: Feature = {
  name: 'actual-energy',
  enabled: true,
  isRoute: true,
  title: {
    en: 'Actual Energy',
    sv: 'Verklig Energi'
  },
  access: ['FEAT_ENERGY-ANALYSIS-ACTUAL_READER'],
  component: 'DashboardDataSectionFlat',
  props: {
    defaultFilters: {
      calendar: {
        quickSelect: 'last30days'
      }
    },
    filters: ['complex', 'realEstate', 'buildings', 'calendar'],
    enabledEntityLevels: ['COMPLEX', 'REAL_ESTATE', 'CONSTRUCTION'],
    dataSources: [
      createDataSource('total', 0),
      createDataSource('totalOverArea', 1),
      createDataSource('bought', 2),
      createDataSource('boughtOverArea', 3),
      createDataSource('production', 4),
      createDataSource('productionOverArea', 5),
      createDataSourceColoredEntities('total', 6),
      createDataSourceColoredEntities('totalOverArea', 7),
      createDataSourceColoredEntities('bought', 8),
      createDataSourceColoredEntities('boughtOverArea', 9),
      createDataSourceColoredEntities('production', 10),
      createDataSourceColoredEntities('productionOverArea', 11),
      createMeasurementDetails('total'),
      createMeasurementDetails('totalOverArea'),
      createMeasurementDetails('bought'),
      createMeasurementDetails('boughtOverArea'),
      createMeasurementDetails('production'),
      createMeasurementDetails('productionOverArea'),
      {
        id: 'energy.measurement.timeseries.electricity',
        type: 'fetch',
        fetch: 'fetch.energy.timeseries',
        transform: {
          id: 'energy.timeseries',
          type: 'ENERGY_BOUGHT_ELECTRICITY_OVER_AREA'
        }
      },
      {
        id: 'energy.measurement.timeseries.heating',
        type: 'fetch',
        fetch: 'fetch.energy.timeseries',
        transform: {
          id: 'energy.timeseries',
          type: 'ENERGY_BOUGHT_DISTRICT_HEATING_OVER_AREA'
        }
      },
      {
        id: 'energy.measurement.timeseries.cooling',
        type: 'fetch',
        fetch: 'fetch.energy.timeseries',
        transform: {
          id: 'energy.timeseries',
          type: 'ENERGY_BOUGHT_COOLING_OVER_AREA'
        }
      }
    ],
    informationFetch: 'fetch.energy.actual.information'
  },
  children: [
    createMeasurement('total', true, '#40A9FF'),
    createMeasurement('totalOverArea', false, '#40A9FF'),
    createMeasurement('bought', false, '#9254DE'),
    createMeasurement('boughtOverArea', false, '#9254DE'),
    createMeasurement('production', false, '#36CFC9'),
    createMeasurement('productionOverArea', false, '#36CFC9'),
    {
      name: 'timeseries',
      enabled: true,
      title: {
        sv: 'Förändring inköpt energi / m² över tid',
        en: 'Change in bought energy / m² over time'
      },
      info: {
        sv: 'Förändring i inköpt energi per ytenhet över tid. Data visas per vecka.',
        en: 'Change in bought energy per area unit over time. Data is shown per week.'
      },
      component: 'CardChart',
      props: {
        enableFullscreen: true,
        chart: {
          type: 'bar',
          codomainFormat: {
            format: {
              type: 'intl-number',
              maximumFractionDigits: 1
            },
            steps: [{ format: { type: 'format-string', format: '%s kWh/m²' } }]
          },
          bucketSize: 'WEEK',
          barFormats: [
            {
              style: 'solid',
              color: '#391085',
              label: {
                sv: 'El',
                en: 'Electricity'
              }
            },
            {
              style: 'solid',
              color: '#9254DE',
              label: {
                sv: 'Fjärrvärme',
                en: 'District heating'
              }
            },
            {
              style: 'solid',
              color: '#EFDBFF',
              label: {
                sv: 'Kyla',
                en: 'Cooling'
              }
            }
          ],
          gradientBelow: true,
          dataSources: [
            'energy.measurement.timeseries.electricity',
            'energy.measurement.timeseries.heating',
            'energy.measurement.timeseries.cooling'
          ],
          missingDataMessage: {
            en: 'Missing data',
            sv: 'Saknar data'
          }
        }
      }
    }
  ]
}
